import { makeAutoObservable, runInAction } from "mobx";
import ApiDataAnalitic from "./ApiDataAnalitic";
import stateAnalyticApp from "../state/stateAnalyticApp";
import stateAnalyticCompany from "../state/stateAnalyticCompany";
import { formatChartDateLabel } from "../utils/analyticsApiUtils";

function isRequestCanceled(e) {
  return e?.code === "ERR_CANCELED" || e?.name === "CanceledError";
}

function swallowCanceledVerifyFetch(promise) {
  if (!promise) return;
  promise.catch((e) => {
    if (!isRequestCanceled(e)) {
      console.error(e);
    }
  });
}

class AnalyticChartData {
  constructor() {
    makeAutoObservable(this);
  }

  resetDataSet = () => {
    this.DataSet = {
      PRINT: {
        dates: {
          labels: [],
          chartData: [],
          series: [],
          data_print: [],
        },
        labelCount: {
          labels: [],
          chartData: [],
          series: [],
          data_print: [],
        },
      },
      SERIALISATION: {
        dates: {
          labels: [],
          chartData: [],
          series: [],
          data_print: [],
        },
        datasetsCount: {
          labels: [],
          slices: [],
          data_print: [],
        },
        data: [
          { data: [], name: "Верификация" },
          { data: [], name: "Брак" },
          { data: [], name: "Инцидент" },
        ],
      },
    };
  };

  DataSet = {
    PRINT: {
      dates: {
        labels: [],
        chartData: [],
        series: [],
        data_print: [],
      },
      labelCount: {
        labels: [],
        chartData: [],
        series: [],
        data_print: [],
      },
    },
    SERIALISATION: {
      dates: {
        labels: [],
        chartData: [],
        series: [],
        data_print: [],
      },
      datasetsCount: {
        labels: [],
        slices: [],
        data_print: [],
      },
      data: [
        { data: [], name: "Верификация" },
        { data: [], name: "Брак" },
        { data: [], name: "Инцидент" },
      ],
    },
  };


  getReportSerialisation = async (type, signal) => {
    const companyId = stateAnalyticCompany.id_company;
    if (!companyId) return undefined;
    try {
      return await ApiDataAnalitic.fetchLabelStatisticSa({
        companyId,
        filterdate: [stateAnalyticApp.from_date, stateAnalyticApp.to_date],
        step: stateAnalyticApp.step_period,
        event: type,
        signal,
      });
    } catch (e) {
      if (e?.isForbidden) {
        stateAnalyticApp.setParameter("noRight", true);
        throw e;
      }
      throw e;
    }
  };

  regIgnoreZero = new RegExp(/\.\d*/gm);
  regIgnoreZeroAndGap = new RegExp(/(\.\d*)|([gG]\d*)/gm);

  _labelDataAbortController = null;
  _zoomRefetch = false;

  abortLabelDataFetch = () => {
    this._labelDataAbortController?.abort();
  };

  resetColorIndex = () => {
    this.colorIndex = 0;
  };
  calcPeriod = async (d_from, d_to) => {
    const date_frome = new Date(d_from).getTime();
    const date_to = new Date(d_to).getTime();
    const day_period = (date_to - date_frome) / 1000 / 60 / 60 / 24;
    if (day_period <= 90) {
      stateAnalyticApp.setParameter("step_period", "d");
    } else if (day_period >= 91 && day_period <= 396) {
      stateAnalyticApp.setParameter("step_period", "mon");
    } else if (day_period >= 397) {
      stateAnalyticApp.setParameter("step_period", "y");
    }
  };
  getLabelData = async () => {
    if (!this._zoomRefetch && this._labelDataAbortController) {
      this._labelDataAbortController.abort();
    }
    const abortController = new AbortController();
    if (!this._zoomRefetch) {
      this._labelDataAbortController = abortController;
    }
    const { signal } = abortController;
    let success = false;
    let verifyFetchPromise = null;

    try {
      stateAnalyticApp.setParameter("isLoading", false);
      this.resetColorIndex();
      this.resetDataSet();
      stateAnalyticApp.setParameter("loaded_printed_dates", false);
      stateAnalyticApp.setParameter("loaded_printed_count", false);
      stateAnalyticApp.setParameter("loaded_verify_dates", false);
      stateAnalyticApp.setParameter("loaded_verify_count", false);
      stateAnalyticApp.resetChartPhases();
      this.calcPeriod(stateAnalyticApp.from_date, stateAnalyticApp.to_date);

      verifyFetchPromise = Promise.all([
        this.getReportSerialisation("v", signal),
        this.getReportSerialisation("d", signal),
        this.getReportSerialisation("i", signal),
      ]);

      const resP = await this.getReportSerialisation("p", signal);
      if (signal.aborted) return false;

      if (resP?.production) {
        stateAnalyticApp.setChartPhase("dates", "processing");
        stateAnalyticApp.setChartPhase("labelCount", "queued");
        await this.createDatePrined(resP.production);
        if (signal.aborted) return false;
        stateAnalyticApp.clearChartPhase("dates");

        stateAnalyticApp.setChartPhase("labelCount", "processing");
        await this.createAllCountPrinted(resP.production);
        if (signal.aborted) return false;
        stateAnalyticApp.clearChartPhase("labelCount");
      } else {
        console.log("Не удалось получить данные по печатным этикеткам");
        stateAnalyticApp.clearChartPhase("dates");
        stateAnalyticApp.clearChartPhase("labelCount");
        stateAnalyticApp.setParameter("loaded_printed_dates", true);
        stateAnalyticApp.setParameter("loaded_printed_count", true);
      }

      const [resV, resD, resI] = await verifyFetchPromise;
      if (signal.aborted) return false;

      this._assignSerialisationResponses(resV, resD, resI);
      stateAnalyticApp.setChartPhase("verifyCount", "processing");
      stateAnalyticApp.setChartPhase("verifyDates", "queued");
      await this.createDateVerificationCounter();
      if (signal.aborted) return false;
      stateAnalyticApp.clearChartPhase("verifyCount");

      stateAnalyticApp.setChartPhase("verifyDates", "processing");
      await this.createDateVerification();
      if (signal.aborted) return false;
      stateAnalyticApp.clearChartPhase("verifyDates");
      runInAction(() => {
        const from = stateAnalyticApp.from_date;
        const to = stateAnalyticApp.to_date;
        stateAnalyticApp.setParameter("from_date_pr_date", from);
        stateAnalyticApp.setParameter("to_date_pr_date", to);
        stateAnalyticApp.setParameter("from_date_pr_count", from);
        stateAnalyticApp.setParameter("to_date_pr_count", to);
        stateAnalyticApp.setParameter("from_date_verify_date", from);
        stateAnalyticApp.setParameter("to_date_verify_date", to);
        stateAnalyticApp.setParameter("from_date_verify_count", from);
        stateAnalyticApp.setParameter("to_date_verify_count", to);
      });
      success = true;
    } catch (e) {
      if (isRequestCanceled(e)) {
        return false;
      }
      console.error(e);
      stateAnalyticApp.clearAllChartPhases();
    } finally {
      swallowCanceledVerifyFetch(verifyFetchPromise);
      if (!signal.aborted) {
        stateAnalyticApp.setParameter("isLoading", true);
      }
    }
    return success;
  };

  getPrintedTemplateData = async (fromDate, toDate, chartId) => {
    this._zoomRefetch = true;
    const savedFrom = stateAnalyticApp.from_date;
    const savedTo = stateAnalyticApp.to_date;
    const savedStep = stateAnalyticApp.step_period;
    const abortController = new AbortController();
    const { signal } = abortController;

    stateAnalyticApp.setParameter("from_date", fromDate);
    stateAnalyticApp.setParameter("to_date", toDate);
    await this.calcPeriod(fromDate, toDate);

    try {
      switch (chartId) {
        case "dates": {
          stateAnalyticApp.setParameter("loaded_printed_dates", false);
          stateAnalyticApp.setChartPhase("dates", "fetching");
          this._resetPrintDates();
          const resP = await this.getReportSerialisation("p", signal);
          if (signal.aborted) return false;
          if (resP?.production) {
            stateAnalyticApp.setChartPhase("dates", "processing");
            await this.createDatePrined(resP.production);
          }
          stateAnalyticApp.clearChartPhase("dates");
          break;
        }
        case "labelCount": {
          stateAnalyticApp.setParameter("loaded_printed_count", false);
          stateAnalyticApp.setChartPhase("labelCount", "fetching");
          this._resetPrintLabelCount();
          const resP = await this.getReportSerialisation("p", signal);
          if (signal.aborted) return false;
          if (resP?.production) {
            stateAnalyticApp.setChartPhase("labelCount", "processing");
            await this.createAllCountPrinted(resP.production);
          }
          stateAnalyticApp.clearChartPhase("labelCount");
          break;
        }
        case "verifyDates": {
          stateAnalyticApp.setParameter("loaded_verify_dates", false);
          stateAnalyticApp.setChartPhase("verifyDates", "fetching");
          this._resetSerialisationDates();
          const [resV, resD, resI] = await Promise.all([
            this.getReportSerialisation("v", signal),
            this.getReportSerialisation("d", signal),
            this.getReportSerialisation("i", signal),
          ]);
          if (signal.aborted) return false;
          this._assignSerialisationResponses(resV, resD, resI);
          stateAnalyticApp.setChartPhase("verifyDates", "processing");
          await this.createDateVerification();
          stateAnalyticApp.clearChartPhase("verifyDates");
          break;
        }
        case "verifyCount": {
          stateAnalyticApp.setParameter("loaded_verify_count", false);
          stateAnalyticApp.setChartPhase("verifyCount", "fetching");
          this._resetSerialisationCount();
          const [resV, resD, resI] = await Promise.all([
            this.getReportSerialisation("v", signal),
            this.getReportSerialisation("d", signal),
            this.getReportSerialisation("i", signal),
          ]);
          if (signal.aborted) return false;
          this._assignSerialisationResponses(resV, resD, resI);
          stateAnalyticApp.setChartPhase("verifyCount", "processing");
          await this.createDateVerificationCounter();
          stateAnalyticApp.clearChartPhase("verifyCount");
          break;
        }
        default:
          return await this.getLabelData();
      }
      return true;
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
        return false;
      }
      console.error(e);
      if (chartId) {
        stateAnalyticApp.clearChartPhase(chartId);
      }
      return false;
    } finally {
      stateAnalyticApp.setParameter("from_date", savedFrom);
      stateAnalyticApp.setParameter("to_date", savedTo);
      stateAnalyticApp.setParameter("step_period", savedStep);
      this._zoomRefetch = false;
    }
  };

  _resetPrintDates = () => {
    this.DataSet.PRINT.dates = {
      labels: [],
      chartData: [],
      series: [],
      data_print: [],
    };
  };

  _resetPrintLabelCount = () => {
    this.DataSet.PRINT.labelCount = {
      labels: [],
      chartData: [],
      series: [],
      data_print: [],
    };
  };

  _resetSerialisationDates = () => {
    this.DataSet.SERIALISATION.dates = {
      labels: [],
      chartData: [],
      series: [],
      data_print: [],
    };
  };

  _resetSerialisationCount = () => {
    this.DataSet.SERIALISATION.datasetsCount = {
      labels: [],
      slices: [],
      data_print: [],
    };
  };

  _assignSerialisationResponses = (resV, resD, resI) => {
    const verifyID = this.DataSet.SERIALISATION.data.find(
      (v) => v.name === "Верификация",
    );
    const defectID = this.DataSet.SERIALISATION.data.find(
      (d) => d.name === "Брак",
    );
    const incidentID = this.DataSet.SERIALISATION.data.find(
      (i) => i.name === "Инцидент",
    );
    if (verifyID) verifyID.data = resV;
    if (defectID) defectID.data = resD;
    if (incidentID) incidentID.data = resI;
  };

  sortDatesFilter = (data) => {
    return data.sort((a, b) => {
      // Преобразуем "dd.mm.yyyy" в "mm/dd/yyyy" для корректного парсинга
      const parseDate = (dateStr) => {
        const [dd, mm, yyOrYyyy] = dateStr.split(".");
        const yyyy =
          yyOrYyyy.length === 2 ? `20${yyOrYyyy}` : yyOrYyyy;
        return new Date(`${mm}/${dd}/${yyyy}`).getTime();
      };

      const aa = parseDate(a);
      const bb = parseDate(b);
      return aa - bb;
    });
  };

  recordLabels = async (data, obj, local) => {
    const dateLabel = formatChartDateLabel(data.timestamp);
    const labels = this.DataSet[`${obj}`][`${local}`].labels;
    if (!labels.includes(dateLabel)) {
      labels.push(dateLabel);
    }
  };

  enumerationProduction = async (
    message,
    obj,
    location,
    func,
    funcColor,
    name,
  ) => {
    message.map((el) => {
      // Перебор площадок
      if (el.data === undefined) return;
      el.data.map((p) => {
        if (p.data !== undefined && p.data !== null) {
          if (p.data.match(/^[wW]\d{2}/g) && !p.data.match(/^[wW]0[hH]0/g)) {
            func(p, obj, location, funcColor, name);
          }
          if (name === "Инцидент") {
            func(p, obj, location, funcColor, "Инцидент");
          }
          if (name === "Брак") {
            func(p, obj, location, funcColor, "Брак");
          }
        } else {
          func(p, obj, location, funcColor, name);
        }
      });
    });
  };

  // Создание структуры этикеток по датам
  createStructureLabelsDates = async (data, obj, location, funcColor) => {
    try {
      let blankStructure = [];
      this.DataSet[`${obj}`][`${location}`].labels.map((l) => {
        blankStructure = [...blankStructure, { timestamp: l, count: 0 }];
      });
      // Собираем структуру
      const nameID = this.DataSet[`${obj}`][`${location}`].data_print.find(
        (d) =>
          !stateAnalyticApp.gap_ignore
            ? data.data.replace(this.regIgnoreZero, "") ===
              d.name.replace(this.regIgnoreZero, "")
            : data.data.replace(this.regIgnoreZeroAndGap, "") ===
              d.name.replace(this.regIgnoreZeroAndGap, ""),
      );
      if (!nameID) {
        const blankStructureID = blankStructure.find(
          (b) =>
            b.timestamp ===
            formatChartDateLabel(data.timestamp),
        );
        if (blankStructureID) {
          blankStructureID.count = data.count;
        }
        this.DataSet[`${obj}`][`${location}`].data_print = [
          ...this.DataSet[`${obj}`][`${location}`].data_print,
          {
            name: !stateAnalyticApp.gap_ignore
              ? data.data.replace(this.regIgnoreZero, "")
              : data.data.replace(this.regIgnoreZeroAndGap, ""),
            backgroundColor: funcColor(),
            production: blankStructure,
          },
        ];
      } else {
        const nameProductionID = nameID.production.find(
          (n) =>
            n.timestamp ===
            formatChartDateLabel(data.timestamp),
        );
        if (nameProductionID) {
          nameProductionID.count = nameProductionID.count + data.count;
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Собираем структуру по количеству
  createStructureLabelsCount = async (data, obj, location, funcColor) => {
    // Собираем структуру
    const nameID = this.DataSet[`${obj}`][`${location}`].data_print.find((d) =>
      !stateAnalyticApp.gap_ignore
        ? data.data.replace(this.regIgnoreZero, "") ===
          d.name.replace(this.regIgnoreZero, "")
        : data.data.replace(this.regIgnoreZeroAndGap, "") ===
          d.name.replace(this.regIgnoreZeroAndGap, ""),
    );
    if (!nameID) {
      this.DataSet[`${obj}`][`${location}`].labels = [
        ...this.DataSet[`${obj}`][`${location}`].labels,
        !stateAnalyticApp.gap_ignore
          ? data.data.replace(this.regIgnoreZero, "")
          : data.data.replace(this.regIgnoreZeroAndGap, ""),
      ];
      this.DataSet[`${obj}`][`${location}`].data_print = [
        ...this.DataSet[`${obj}`][`${location}`].data_print,
        {
          name: !stateAnalyticApp.gap_ignore
            ? data.data.replace(this.regIgnoreZero, "")
            : data.data.replace(this.regIgnoreZeroAndGap, ""),
          backgroundColor: funcColor(),
          production: [
            {
              name: !stateAnalyticApp.gap_ignore
                ? data.data.replace(this.regIgnoreZero, "")
                : data.data.replace(this.regIgnoreZeroAndGap, ""),
              count: data.count,
            },
          ],
        },
      ];
    } else {
      let countID = nameID.production.find((c) =>
        c.name !== !stateAnalyticApp.gap_ignore
          ? data.data.replace(this.regIgnoreZero, "")
          : data.data.replace(this.regIgnoreZeroAndGap, ""),
      );

      if (countID) {
        countID.count = countID.count + data.count;
      }
    }
    this.DataSet[`${obj}`][`${location}`].labels = ["Этикетки"];
  };

  // Создание структуры верификации

  // Cобираем chartData печать по дням / общее количество
  createDataSetDates = async (obj, location) => {
    const chartLocation = this.DataSet[`${obj}`][`${location}`];
    const series = chartLocation.data_print.map((ds) => ({
      key: ds.name,
      color: ds.backgroundColor,
      fill: ds.backgroundColor + "b0",
    }));
    const chartData = chartLocation.labels.map((label, index) => {
      const point = { label };
      chartLocation.data_print.forEach((ds) => {
        point[ds.name] = ds.production[index]?.count ?? 0;
      });
      return point;
    });
    runInAction(() => {
      chartLocation.series = series;
      chartLocation.chartData = chartData;
    });
  };
  //  Собираем chartData печать общее
  createDataSetCount = async (obj, location) => {
    await this.createDataSetDates(obj, location);
  };
  //  Собираем slices сериализация общее
  createDataSetCountSerialisation = async (obj, location) => {
    const slices = this.DataSet[`${obj}`][`${location}`].map((ds) => {
      let sum = 0;
      ds.data.production.forEach((s) => {
        if (s.data !== undefined) {
          s.data.forEach((i) => {
            sum += i.count;
          });
        }
      });
      const stroke =
        ds.name === "Верификация"
          ? "#5ccd4a"
          : ds.name === "Брак"
            ? "#cd2b2b"
            : "#4acdcc";
      return {
        name: ds.name,
        value: sum,
        fill: stroke + "b0",
        stroke,
      };
    });

    runInAction(() => {
      this.DataSet[`${obj}`].datasetsCount.slices = slices;
    });
  };

  // По датам
  createDatePrined = async (data) => {
    try {
      const color_chart = [
        "#2d77b7",
        "#cd2b2b",
        "#5ccd4a",
        "#4acdcc",
        "#814fb7",
        "#b74f9d",
        "#dfde11",
        "#ff7e07",
        "#1cff07",
        "#07e7ff",
        "#074fff",
        "#ff07c1",
        "#ff0707",
        "#181818",
      ];
      let colorIndex = 0;
      const selectColor = () => {
        const color = color_chart[colorIndex];
        colorIndex = colorIndex + 1;
        return color;
      };

      try {
        // Собираем даты
        await this.enumerationProduction(
          data,
          "PRINT",
          "dates",
          this.recordLabels,
        );
        this.sortDatesFilter(this.DataSet.PRINT.dates.labels);
        // Собираем структуру для datasets
        await this.enumerationProduction(
          data,
          "PRINT",
          "dates",
          this.createStructureLabelsDates,
          selectColor,
        );
        // Собираем datset для графиков
        await this.createDataSetDates("PRINT", "dates");
      } catch (e) {
        console.error(e);
      } finally {
        stateAnalyticApp.setParameter("loaded_printed_dates", true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Общее количество
  createAllCountPrinted = async (data) => {
    const color_chart = [
      "#2d77b7",
      "#cd2b2b",
      "#5ccd4a",
      "#4acdcc",
      "#814fb7",
      "#b74f9d",
      "#dfde11",
      "#ff7e07",
      "#1cff07",
      "#07e7ff",
      "#074fff",
      "#ff07c1",
      "#ff0707",
      "#181818",
    ];
    let colorIndex = 0;
    const selectColor = () => {
      const color = color_chart[colorIndex];
      colorIndex = colorIndex + 1;
      return color;
    };
    // Собираем структуру для dataset
    await this.enumerationProduction(
      data,
      "PRINT",
      "labelCount",
      this.createStructureLabelsCount,
      selectColor,
    );
    // Собираем datset для графиков
    await this.createDataSetCount("PRINT", "labelCount");
    stateAnalyticApp.setParameter("loaded_printed_count", true);
  };

  createAllCountSerialisationLabels = async () => {
    this.DataSet.SERIALISATION.datasetsCount.labels = [];
    this.DataSet.SERIALISATION.data.map((d) => {
      if (d.name !== undefined) {
        this.DataSet.SERIALISATION.datasetsCount.labels = [
          ...this.DataSet.SERIALISATION.datasetsCount.labels,
          d.name,
        ];
      }
    });
  };

  createStructureLabelsDatesSerial = async (
    data,
    obj,
    location,
    funcColor,
    name,
  ) => {
    try {
      if (!name) {
        console.error("Name is missing for data:", data);
        return;
      }
      let blankStructure = [];
      const labels = this.DataSet[`${obj}`][`${location}`].labels ?? [];
      labels.map((l) => {
        blankStructure = [...blankStructure, { timestamp: l, count: 0 }];
      });
      const nameID = this.DataSet[`${obj}`][`${location}`].data_print.find(
        (n) => n.name === name,
      );
      if (!nameID) {
        const blankStructureID = blankStructure.find(
          (b) =>
            b.timestamp ===
            formatChartDateLabel(data.timestamp),
        );
        if (blankStructureID) {
          blankStructureID.count = blankStructureID.count + data.count;
        }
        this.DataSet[`${obj}`][`${location}`].data_print = [
          ...this.DataSet[`${obj}`][`${location}`].data_print,
          {
            name: name,
            backgroundColor:
              name === "Верификация"
                ? "#5ccd4a"
                : name === "Брак"
                  ? "#cd2b2b"
                  : name === "Инцидент"
                    ? "#4acdcc"
                    : funcColor(),
            production: blankStructure,
          },
        ];
      } else {
        const nameProductionID = nameID.production.find(
          (n) =>
            n.timestamp ===
            formatChartDateLabel(data.timestamp),
        );
        if (nameProductionID) {
          nameProductionID.count = nameProductionID.count + data.count;
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  createDateVerification = async () => {
    const color_chart = [
      "#2d77b7",
      "#cd2b2b",
      "#5ccd4a",
      "#4acdcc",
      "#814fb7",
      "#b74f9d",
      "#dfde11",
      "#ff7e07",
      "#1cff07",
      "#07e7ff",
      "#074fff",
      "#ff07c1",
      "#ff0707",
      "#181818",
    ];
    let colorIndex = 0;
    const selectColor = () => {
      const color = color_chart[colorIndex];
      colorIndex = colorIndex + 1;
      return color;
    };
    try {
      await Promise.all(
        this.DataSet.SERIALISATION.data.map((event) =>
          this.enumerationProduction(
            event.data.production,
            "SERIALISATION",
            "dates",
            this.recordLabels,
          ),
        ),
      );
      await Promise.all(
        this.DataSet.SERIALISATION.data.map((event) =>
          this.enumerationProduction(
            event.data.production,
            "SERIALISATION",
            "dates",
            this.createStructureLabelsDatesSerial,
            selectColor,
            event.name,
          ),
        ),
      );
      this.sortDatesFilter(this.DataSet.SERIALISATION.dates.labels);
      await this.createDataSetDates("SERIALISATION", "dates");
      stateAnalyticApp.setParameter("loaded_verify_dates", true);
    } catch (e) {
      console.error(e);
    }
  };

  createDateVerificationCounter = async () => {
    const color_chart = [
      "#2d77b7",
      "#cd2b2b",
      "#5ccd4a",
      "#4acdcc",
      "#814fb7",
      "#b74f9d",
      "#dfde11",
      "#ff7e07",
      "#1cff07",
      "#07e7ff",
      "#074fff",
      "#ff07c1",
      "#ff0707",
      "#181818",
    ];
    let colorIndex = 0;
    const selectColor = () => {
      const color = color_chart[colorIndex];
      colorIndex = colorIndex + 1;
      return color;
    };
    try {
      this.createAllCountSerialisationLabels();
      await Promise.all(
        this.DataSet.SERIALISATION.data.map((event) =>
          this.enumerationProduction(
            event.data.production,
            "SERIALISATION",
            "datasetsCount",
            this.createStructureLabelsDatesSerial,
            selectColor,
            event.name,
          ),
        ),
      );
      await this.createDataSetCountSerialisation("SERIALISATION", "data");
      stateAnalyticApp.setParameter("loaded_verify_count", true);
    } catch (e) {
      console.error(e);
    }
  };
}

export default new AnalyticChartData();
